import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeleteDayPlan } from './template-modules.model';

// ─── Borrar un día de un módulo que tiene sesiones (S1) ─────────────────────
// Un día sin sesiones se borra directo; este diálogo aparece solo si el día
// tiene sesiones, y pregunta qué hacer con ellas: moverlas a otro día del
// módulo (por defecto, al siguiente, o al anterior si es el último) o borrarlas.

export function ModuleDayDeleteDialog({
  open,
  day,
  sessionCount,
  dayLabels,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  /** Posición del día que se borra. */
  day: number;
  sessionCount: number;
  /** La etiqueta de cada día del módulo, en orden. */
  dayLabels: string[];
  onCancel: () => void;
  onConfirm: (plan: DeleteDayPlan) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const k = 'templateEditor.modules.deleteDay';
  const others = dayLabels.map((label, i) => ({ label, i })).filter((d) => d.i !== day);
  const defaultTarget = day + 1 < dayLabels.length ? day + 1 : day - 1;
  const [kind, setKind] = useState<DeleteDayPlan['kind']>('move');
  const [to, setTo] = useState(defaultTarget);

  useEffect(() => {
    if (!open) return;
    setKind(others.length > 0 ? 'move' : 'drop');
    setTo(defaultTarget);
  }, [open, day, others.length, defaultTarget]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t(`${k}.title`, { n: day + 1 })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">{t(`${k}.body`, { count: sessionCount })}</Typography>
          {others.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t(`${k}.onlyDay`)}
            </Typography>
          ) : (
            <RadioGroup
              value={kind}
              onChange={(e) => setKind(e.target.value as DeleteDayPlan['kind'])}
            >
              <FormControlLabel
                value="move"
                control={<Radio size="small" />}
                label={t(`${k}.move`, { count: sessionCount })}
              />
              <FormControl size="small" sx={{ ml: 4, mb: 1 }} disabled={kind !== 'move'}>
                <InputLabel id="delete-day-target">{t(`${k}.target`)}</InputLabel>
                <Select
                  labelId="delete-day-target"
                  label={t(`${k}.target`)}
                  value={to}
                  onChange={(e) => setTo(Number(e.target.value))}
                >
                  {others.map((d) => (
                    <MenuItem key={d.i} value={d.i}>
                      {d.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                value="drop"
                control={<Radio size="small" />}
                label={t(`${k}.drop`, { count: sessionCount })}
              />
            </RadioGroup>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t(`${k}.cancel`)}</Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => onConfirm(kind === 'move' ? { kind: 'move', to } : { kind: 'drop' })}
        >
          {t(`${k}.confirm`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
